/**
 * De dónde salen los tres datos que se imprimen en el certificado, y cómo se llama el
 * archivo que se descarga.
 *
 * Está aparte del motor del PDF a propósito: `generarCertificado` recibe tres strings
 * ya resueltos y no sabe nada de Supabase ni del catálogo de cursos, así que se puede
 * probar sin sesión y sin app.
 */

import { nombreDelUsuarioActual } from '../../../lib/nombreUsuario';
import { slugifyVariantName } from '../../../utils/downloadFileName';
import { cargarAprobadoEn, type CursoAcademy } from '../academyApi';
import type { DatosCertificado } from './generarCertificado';

/**
 * Deja presentable el nombre que va impreso.
 *
 * `nombreDelUsuario` cae a la parte local del mail cuando el registro no dejó un
 * nombre, y eso en un certificado se ve como `juan.perez88`. Un handle —sin espacios,
 * con puntos, guiones o números— se convierte en "Juan Perez"; un nombre de verdad se
 * deja EXACTAMENTE como la persona lo escribió, mayúsculas incluidas: nadie quiere que
 * le "corrijan" el apellido en su certificado.
 */
export function nombreParaCertificado(crudo: string): string {
  const nombre = crudo.trim().replace(/\s+/g, ' ');
  if (!nombre) return 'Alumno';
  const pareceHandle = !nombre.includes(' ') && /[._-]|\d/.test(nombre);
  if (!pareceHandle) return nombre;
  const palabras = nombre
    .replace(/\d+/g, ' ')
    .split(/[._-]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (palabras.length === 0) return nombre;
  return palabras
    .map((p) => p.charAt(0).toLocaleUpperCase('es-AR') + p.slice(1))
    .join(' ');
}

/**
 * Junta los datos del certificado de un curso aprobado.
 *
 * Devuelve `null` si no hay sesión: el nombre es el dato central del certificado, y
 * emitir uno a nombre de "Usuario" porque el token venció es peor que decir que hay
 * que volver a entrar.
 *
 * Si el curso figura aprobado pero no aparece ningún intento aprobado —progreso
 * cargado a mano, o un `marcarCursoAprobado` administrativo— se usa la fecha de hoy:
 * un certificado sin fecha de finalización no es un certificado.
 */
export async function datosDelCertificado(
  curso: CursoAcademy,
): Promise<DatosCertificado | null> {
  const [nombre, aprobadoEn] = await Promise.all([
    nombreDelUsuarioActual(),
    cargarAprobadoEn(curso.id),
  ]);
  if (!nombre) return null;
  return {
    nombre: nombreParaCertificado(nombre),
    curso: curso.titulo,
    fecha: aprobadoEn ?? Date.now(),
  };
}

/** `certificado_atlas_academy_como_vender_mas.pdf`. */
export function nombreDeArchivoCertificado(curso: CursoAcademy): string {
  return `certificado_atlas_academy_${slugifyVariantName(curso.titulo)}.pdf`;
}
